import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #pendingCartUpdates = new Set();
  #cartSyncController = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.addEventListener('pageshow', this.#handlePageShow);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#cartSyncController?.abort();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#syncCart();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handlePageShow = (event) => {
    if (event.persisted && this.#themeDrawer?.isOpen) this.#syncCart();
  };

  // A restored page can have a current cart badge but stale drawer HTML.
  // Revalidate shared drawer content, and never apply an older request.
  #syncCart = async () => {
    this.#cartSyncController?.abort();
    const controller = new AbortController();
    this.#cartSyncController = controller;
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-cart-sync-error]')?.remove();

    try {
      await Promise.allSettled([...this.#pendingCartUpdates]);
      if (controller.signal.aborted || !this.isConnected) return;

      const sectionId = this.querySelector('cart-items-component')?.dataset.sectionId;
      if (!sectionId) throw new Error('Cart drawer section id missing');
      // The /cart template intentionally omits the drawer; render in this page's context.
      const url = new URL(window.location.href);
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected) return;
      const rendered = new DOMParser().parseFromString(html, 'text/html');
      if (!rendered.querySelector('[data-hydration-key="cart-drawer-inner"] cart-items-component')) {
        throw new Error('Cart drawer content missing from section response');
      }

      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      const count = Number(this.querySelector('[ref="cartItemCount"]')?.textContent ?? 0);
      for (const icon of document.querySelectorAll('cart-icon')) {
        icon.renderCartBubble?.(count, false);
      }
      this.#updateStickyState();
    } catch (error) {
      if (controller.signal.aborted || !this.isConnected) return;
      console.warn('[cart-drawer] Unable to refresh cart:', error);
      const message = document.createElement('p');
      message.dataset.cartSyncError = '';
      message.setAttribute('role', 'alert');
      message.textContent = 'Unable to refresh your cart. ';
      const link = document.createElement('a');
      link.href = Theme.routes.cart_url;
      link.textContent = 'View cart';
      message.append(link);
      this.append(message);
    } finally {
      if (this.#cartSyncController === controller) this.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    this.#cartSyncController?.abort();
    if (event.promise) {
      this.#pendingCartUpdates.add(event.promise);
      const settled = () => this.#pendingCartUpdates.delete(event.promise);
      event.promise.then(settled, settled);
    }
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          this.#syncCart();
          requestAnimationFrame(() => this.#updateStickyState());
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
        this.#syncCart();
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
